import { Permissions, User } from '@appsvio/jira-rest-api-client';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
    IncidentBannerDto,
    IncidentBannerSaveDto,
    IncidentBannerStatus,
} from '../../../../../src/services/incident-banners/model';
import {
    ADMINISTER_PROJECTS_PERMISSION,
    BROWSE_PROJECTS_PERMISSION,
    GLOBAL_ADMIN__PERMISSION,
    SERVICE_DESK_AGENT_PERMISSION,
} from '../../../../common/constants';
import { getIncidentBannersRelatedPermissions, getIssueIdentifiers, getUsersById } from '../../../api/jira';
import {
    deleteIncidentBanner,
    getBannersForProjectAndStatus,
    saveIncidentBanner,
    updateIncidentBanner,
} from '../../../bridge/incidentBanners';
import { MappedIssue } from '../interfaces';

import { RootState } from './store';

interface PanelState {
    activeBannerKey: string | undefined;
    banners: IncidentBannerDto[];
    canEdit: boolean;
    canView: boolean;
    editData: IncidentBannerDto | undefined;
    isDeleteBannerModalOpen: boolean;
    isFormOpened: boolean;
    isLoading: boolean;
    issues: MappedIssue[];
    userPermissions: Permissions;
    users: User[];
}

const initialState = {
    activeBannerKey: undefined,
    banners: [],
    canView: false,
    canEdit: false,
    editData: undefined,
    isDeleteBannerModalOpen: false,
    isFormOpened: false,
    isLoading: true,
    issues: [],
    userPermissions: {},
    users: [],
} as unknown as PanelState;

const orderByRecentDate = (banners: IncidentBannerDto[]): IncidentBannerDto[] =>
    banners.sort((a, b) => {
        const aTimestamp = a.updatedAt ?? a.createdAt;
        const bTimestamp = b.updatedAt ?? b.createdAt;

        return bTimestamp - aTimestamp;
    });

const canViewBanners = (userPermissions: Permissions): boolean =>
    Object.values(userPermissions.permissions).some(
        permission => permission.key === BROWSE_PROJECTS_PERMISSION && permission.havePermission
    );

const canEditBanners = (userPermissions: Permissions): boolean => {
    const hasProjectPermission = Object.values(userPermissions.permissions).some(
        permission =>
            (permission.key === ADMINISTER_PROJECTS_PERMISSION || permission.key === SERVICE_DESK_AGENT_PERMISSION) &&
            permission.havePermission
    );
    const hasGlobalPermission = userPermissions.permissions[GLOBAL_ADMIN__PERMISSION]?.havePermission === true;

    return hasProjectPermission || hasGlobalPermission;
};

const getUniqueUserIds = (banners: IncidentBannerDto[]): string[] => {
    const ids = banners.flatMap(banner => [banner.createAccountId, banner.updateAccountId].filter(id => id) as string[]);

    return [...new Set(ids)];
};

const getUniqueIssueIds = (banners: IncidentBannerDto[]): string[] => {
    const ids = banners.map(banner => banner.issueId);

    return [...new Set(ids)];
};

export const fetchAll = createAsyncThunk(
    'incidentBanners/fetchAll',
    async ({ projectId }: { projectId: string }) => {
        const banners = (await getBannersForProjectAndStatus(projectId, IncidentBannerStatus.ACTIVE)) as IncidentBannerDto[];
        const userIds = getUniqueUserIds(banners);
        const issueIds = getUniqueIssueIds(banners);
        const [userPermissions, issues, users] = await Promise.all([
            getIncidentBannersRelatedPermissions(projectId),
            issueIds.length ? getIssueIdentifiers(issueIds) : Promise.resolve([]),
            getUsersById(userIds),
        ]);

        return { banners, users, userPermissions, issues };
    }
);

export const saveBanner = createAsyncThunk(
    'incidentBanners/saveBanner',
    async (banner: IncidentBannerSaveDto, { dispatch }) => {
        await saveIncidentBanner(banner);
        await dispatch(fetchAll({ projectId: banner.projectId }));
    }
);

export const deleteBanner = createAsyncThunk(
    'incidentBanners/deleteBanner',
    async ({ key, projectId }: { key: string; projectId: string }, { getState }) => {
        await deleteIncidentBanner(key, projectId);
        const state = (getState() as RootState).incidentBanners;
        const updatedBanners = state.banners.filter((banner: IncidentBannerDto) => banner.key !== key);

        return updatedBanners;
    }
);

export const updateBanner = createAsyncThunk(
    'incidentBanners/updateBanner',
    async (data: IncidentBannerSaveDto, { dispatch, getState }) => {
        const state = (getState() as RootState).incidentBanners;
        const updateBannerKey = state.activeBannerKey;

        if (updateBannerKey) {
            await updateIncidentBanner(updateBannerKey, data);
            await dispatch(fetchAll({ projectId: data.projectId }));
        }
    }
);

export const incidentBannersSlice = createSlice({
    name: 'incidentBanners',
    initialState,
    reducers: {
        updateFormOpened: (state, action: PayloadAction<boolean>) => {
            state.isFormOpened = action.payload;
        },
        updateEditData: (state, action: PayloadAction<{ activeBannerKey: string; editData: IncidentBannerDto }>) => {
            const { activeBannerKey, editData } = action.payload;
            state.activeBannerKey = activeBannerKey;
            state.editData = editData;
            state.isFormOpened = true;
        },
        handleFormCancel: (state, action: PayloadAction<boolean>) => {
            const isEdit = action.payload;
            state.isFormOpened = false;

            if (isEdit) {
                state.editData = undefined;
                state.activeBannerKey = undefined;
            }
        },
        handleBannerDeletion: (state, action: PayloadAction<string>) => {
            state.activeBannerKey = action.payload;
            state.isDeleteBannerModalOpen = true;
        },
        updateDeleteBannerModalOpen: (state, action: PayloadAction<boolean>) => {
            state.isDeleteBannerModalOpen = action.payload;
        },
    },
    extraReducers: builder => {
        builder.addCase(
            fetchAll.fulfilled,
            (
                state,
                action: PayloadAction<{
                    banners: IncidentBannerDto[];
                    issues: MappedIssue[];
                    userPermissions: Permissions;
                    users: User[];
                }>
            ) => {
                const { banners, issues, users, userPermissions } = action.payload;
                const canEdit = canEditBanners(userPermissions);
                state.banners = orderByRecentDate(banners);
                state.users = users;
                state.userPermissions = userPermissions;
                state.issues = issues;
                state.canEdit = canEdit;
                state.canView = canViewBanners(userPermissions);
                state.isLoading = false;
            }
        );
        builder.addCase(fetchAll.pending, state => {
            state.isLoading = true;
        });
        builder.addCase(fetchAll.rejected, state => {
            state.isLoading = false;
        });
        builder.addCase(saveBanner.fulfilled, state => {
            state.isLoading = false;
            state.isFormOpened = false;
        });
        builder.addCase(saveBanner.pending, state => {
            state.isLoading = true;
        });
        builder.addCase(saveBanner.rejected, state => {
            state.isLoading = false;
            state.isFormOpened = false;
        });
        builder.addCase(updateBanner.fulfilled, state => {
            state.isLoading = false;
            state.isFormOpened = false;
            state.activeBannerKey = undefined;
            state.editData = undefined;
        });
        builder.addCase(updateBanner.pending, state => {
            state.isLoading = true;
        });
        builder.addCase(updateBanner.rejected, state => {
            state.isLoading = false;
            state.isFormOpened = false;
        });
        builder.addCase(deleteBanner.fulfilled, (state, action: PayloadAction<IncidentBannerDto[]>) => {
            state.banners = action.payload;
            state.activeBannerKey = undefined;
            state.isDeleteBannerModalOpen = false;
            state.isLoading = false;
        });
        builder.addCase(deleteBanner.pending, state => {
            state.isLoading = true;
        });
        builder.addCase(deleteBanner.rejected, state => {
            state.isLoading = false;
        });
    },
});

export const { handleFormCancel, handleBannerDeletion, updateEditData, updateDeleteBannerModalOpen, updateFormOpened } =
    incidentBannersSlice.actions;

export default incidentBannersSlice.reducer;
